// All-WebGL streaming pipeline — untile shader + LUT helpers (STREAM_PIPELINE.md).
//
// The shared untile math: unpack a decoded video atlas (one luma plane, tiled) into
// the channel-major geom + color/coeff plane textures the lift pass1 consumes. The
// untile is an INVERSE GATHER (a fragment shader can't scatter): per OUTPUT texel
// (ch, dy, dx) find its tile + source pixel, sample the atlas, dequant, signed-log
// expand (compand), and the inverse 2×2 polyphase de-interleave.
//
// `UNTILE_FS` + `buildUntileLuts` + `untileBufSel` are consumed by the live
// `render/spark/ThreeUntile.ts` (three-native). Luma modes: "expanded" — a real
// decoded VideoFrame (browser YUV→RGB gives range-expanded `.r`); "raw" — a
// synthetic atlas carrying the raw Y byte in R.

// Compile-time upper bound on channels per output buffer, and the GLSL array size of
// `lut`/`loRng` below. geom is 11; coefficients are K*3 — 27 at deg-2, 48 at deg-3.
// Overflowing it is silent on its own: a Float32Array write past the end is a no-op
// in JS and the shader cannot index past its array bound, so the excess planes would
// simply be missing and read as a fit problem. buildUntileLuts throws instead.
const MAXCH = 48;

export interface UntileTile {
  branch: string; kind: string; ch: number; xf?: string;
  /** Tile origin in ATLAS PIXELS, and this tile's edge length. Sent by the server so
   *  the atlas need not be a uniform grid: a bundle whose branches were exported at
   *  different uv_res (768 head / 256 body is a real on-device shape) packs each
   *  branch at its own size. Absent from an older server's meta, in which case the
   *  origin is derived from the plane index and the single `tile` size, exactly as
   *  before. */
  x?: number; y?: number; size?: number;
}
export interface UntileMeta {
  tile: number; cols: number; w: number; h: number;
  compand_a?: number; y_lo?: number; y_range?: number;
  layout: UntileTile[]; norm: [number, number][]; K?: number;
}

const XF_FLAG: Record<string, number> = { none: 0, cmp: 1, deint: 2, cd: 3 };
// bufSel: 0=head/geom 1=head/(color|sh) 2=body/geom 3=body/(color|sh)
export const untileBufSel = (L: UntileTile) => (L.branch === "body" ? 2 : 0) + (L.kind === "geom" ? 0 : 1);
const bufSel = untileBufSel;

export interface UntileBufLut { maxCh: number; lut: Float32Array; loRng: Float32Array; tile: number; }
/** Per-bufSel (0..3) channel count + lookup tables (row,col,xf,valid) / (lo,rng),
 *  sized to MAXCH. Consumed by ThreeUntile + unit-tested directly. */
export function buildUntileLuts(meta: UntileMeta): UntileBufLut[] {
  const maxCh = [11, 3, 11, 3];
  for (const L of meta.layout) maxCh[bufSel(L)] = Math.max(maxCh[bufSel(L)], L.ch + 1);
  // Loud, not silent: past MAXCH the LUT writes below would be dropped by the
  // typed array and the shader's fixed-size uniform arrays could not address the
  // planes, rendering a plausible-looking image with SH bands missing.
  const over = maxCh.findIndex((n) => n > MAXCH);
  if (over >= 0) {
    throw new Error(
      `untile: stream declares ${maxCh[over]} channels for buffer ${over} but MAXCH is ` +
      `${MAXCH} — raise MAXCH in WebGlUntile.ts (and the lut/loRng GLSL array sizes) ` +
      `to match. Rendering would silently drop the excess planes.`);
  }
  const bufs: UntileBufLut[] = [];
  for (let s = 0; s < 4; s++) {
    const lut = new Float32Array(MAXCH * 4);
    const loRng = new Float32Array(MAXCH * 2);
    // Every plane in one buffer belongs to one branch (bufSel splits head from body),
    // so a buffer has a single tile size even when the atlas does not.
    let tile = meta.tile;
    meta.layout.forEach((L, t) => {
      if (bufSel(L) !== s) return;
      // Pixel origin, from the server when it sends one; otherwise the old uniform-grid
      // derivation, so a newer client still reads an older server's stream.
      const size = L.size ?? meta.tile;
      const x = L.x ?? (t % meta.cols) * meta.tile;
      const y = L.y ?? Math.floor(t / meta.cols) * meta.tile;
      tile = size;
      const lo = meta.norm[t][0], rng = (meta.norm[t][1] - meta.norm[t][0]) || 1e-12;
      const c = L.ch;
      lut[c*4] = x; lut[c*4+1] = y; lut[c*4+2] = XF_FLAG[L.xf ?? "none"]; lut[c*4+3] = 1;
      loRng[c*2] = lo; loRng[c*2+1] = rng;
    });
    bufs.push({ maxCh: maxCh[s], lut, loRng, tile });
  }
  return bufs;
}

export const UNTILE_MAXCH = MAXCH;

// Shared untile fragment shader (three-native ThreeUntile strips the #version
// directive and reuses it). One R32F output per (bufSel) plane.
export const UNTILE_FS = `#version 300 es
precision highp float;
precision highp int;
uniform highp sampler2D atlas;     // decoded frame; .r = luma (raw byte/255 or range-expanded)
uniform int T, HW, maxCh, outW, lumaMode;   // lumaMode: 0=expanded, 1=raw byte
uniform float A, yLo, yRange;
uniform vec4 lut[${MAXCH}];        // per-ch: (originX, originY, xf, valid) in ATLAS PIXELS
uniform vec2 loRng[${MAXCH}];      // per-ch: (lo, rng)
out float o;
void main(){
  int i = int(gl_FragCoord.y)*outW + int(gl_FragCoord.x);
  if (i >= maxCh*HW){ o = 0.0; return; }
  int ch = i / HW; int p = i - ch*HW;
  int dy = p / T; int dx = p - dy*T;
  vec4 rc = lut[ch];
  if (rc.w < 0.5){ o = 0.0; return; }      // no tile for this channel
  int ox = int(rc.x), oy = int(rc.y), xf = int(rc.z);
  int hf = T >> 1;
  int x = dx, y = dy;
  if ((xf & 2) != 0){                       // inverse 2×2 polyphase de-interleave
    int py = dy & 1; y = (dy >> 1) + py*hf;
    int qx = dx & 1; x = (dx >> 1) + qx*hf;
  }
  // Pixel origin, not row*T/col*T: the atlas is not necessarily a uniform grid.
  float z = texelFetch(atlas, ivec2(ox + x, oy + y), 0).r;
  if (lumaMode == 1) z = (z*255.0 - yLo) / yRange;   // raw byte → normalized
  float v = z * loRng[ch].y + loRng[ch].x;
  if ((xf & 1) != 0) v = sign(v) * A * (exp(abs(v)) - 1.0);   // signed-log expand
  o = v;
}`;
